import { useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import moment from 'moment'
import { ComponentProps, useMemo, useState } from 'react'

import { Payment } from '../../models/Payment'
import { Reader } from '../../models/Reader'
import { Source } from '../../models/Source'
import { User } from '../../models/User'
import { isSameDMY } from '../../utils/dates'
import { Icon, IconNameAndCategoryString } from '../Icon'
import { Spinner } from '../Spinner'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Card from '../app/design/Card'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import SegmentedControl from '../app/design/SegmentedControl'
import { ContactAvatar } from '../contacts/ContactAvatar'
import { Checkbox } from '../common/fields/Checkbox'
import { Chip } from '../common/fields/Chip'
import { DatePicker } from '../common/fields/DatePicker'
import { TextField } from '../common/fields/TextField'
import { markPaidPaymentOptions } from '../settings/Options'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { CardModal } from './CardModal'
import { NewChargeReceiptView } from './NewChargeReceiptView'

type Props = {
    payment: Payment
    readers: Reader[]
    selectedReader?: Reader
    canPayViaOther: boolean
    onSelectReader: (reader?: Reader) => void
    onUpdatePayment: (payment: Payment) => void
}

const getIconFromCardBrand = (cardBrand?: string): IconNameAndCategoryString => {
    switch (cardBrand) {
        case 'visa':
            return 'visa.payment'
        case 'mastercard':
            return 'mastercard.payment'
        case 'amex':
        case 'american express':
            return 'amex.payment'
        case 'discover':
            return 'discover.payment'
        case 'apple pay':
            return 'applePay.payment'
        default:
            return 'bankCard.large'
    }
}

function PayByCard({
    sources,
    onSelectSource,
    selectedSourceID,
    payment,
}: {
    sources: Source[]
    onSelectSource: (source: Source) => void
    selectedSourceID?: string
    payment: Payment
}) {
    const [isAddCardModalOpen, setIsAddCardModalOpen] = useState(false)
    const [sourcesToDisplay, setSourcesToDisplay] = useState<Source[]>(
        sources.filter((source) => source.type === 'credit' || source.type === 'debit')
    )
    const queryClient = useQueryClient()

    return (
        <>
            <div className="flex w-full flex-col items-center justify-between gap-3">
                {payment.client !== undefined && (
                    <div
                        className={clsx([
                            'relative z-10 flex w-full items-center justify-between',
                            'cursor-pointer before:absolute before:inset-x-[-16px] before:-top-4 before:hidden before:rounded-lg before:bg-black/4 before:content-[""] not-first:before:-z-10 hover:before:block',
                            {
                                'before:-bottom-3': sourcesToDisplay.length > 0,
                                'before:-bottom-4': sourcesToDisplay.length === 0,
                            },
                        ])}
                        onClick={() => setIsAddCardModalOpen(true)}
                    >
                        <div className="flex items-center gap-4">
                            <Icon icon="bankCard.large" background="round" className="shrink-0" />
                            <div className="flex flex-col gap-1">
                                <Label size="md">Cards</Label>
                                <Paragraph size="sm" className="text-gray-600">
                                    Select
                                </Paragraph>
                            </div>
                        </div>
                        <Button
                            size="small"
                            intent="tertiaryLink"
                            className="z-10 p-0"
                            onClick={() => setIsAddCardModalOpen(true)}
                        >
                            Add
                        </Button>
                    </div>
                )}

                {sourcesToDisplay.length > 0 && (
                    <div className="flex w-full flex-col gap-3">
                        {sourcesToDisplay.map((source) => (
                            <div
                                key={source.id}
                                className={clsx(
                                    'ml-16 flex cursor-pointer items-center justify-between rounded-lg p-4 outline outline-gray-300 hover:bg-black/4 active:bg-black/8',
                                    {
                                        'outline-secondary-400 outline-2':
                                            selectedSourceID === source.id,
                                    }
                                )}
                                onClick={() => onSelectSource(source)}
                            >
                                <div className="flex items-center justify-start gap-4">
                                    <Icon
                                        icon={getIconFromCardBrand(source.cardBrand?.toLowerCase())}
                                    />
                                    <div className="flex flex-col gap-1">
                                        <Paragraph size="md" className="text-gray-700">
                                            {source.displayName}
                                        </Paragraph>
                                    </div>
                                </div>
                                <Checkbox
                                    style="radio"
                                    isChecked={selectedSourceID === source.id}
                                    className="shrink-0"
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <CardModal
                isOpen={isAddCardModalOpen}
                onClose={() => setIsAddCardModalOpen(false)}
                onSuccess={{
                    action: 'create-source',
                    client: payment.client,
                    callback: (source) => {
                        queryClient.invalidateQueries({ queryKey: ['sources', payment.client?.id] })
                        setSourcesToDisplay((s) => [...s, source])
                        onSelectSource(source)
                        setIsAddCardModalOpen(false)
                    },
                }}
            />
        </>
    )
}

function PayByCardReader({
    readers,
    onSelectReader,
    selectedReaderID,
}: {
    readers: Reader[]
    onSelectReader: (reader: Reader) => void
    selectedReaderID?: string
}) {
    return (
        <div className="flex gap-4">
            <Icon icon="cardReader.large" background="round" className="shrink-0" />
            <div className="flex w-full flex-col gap-3">
                <div>
                    <Label size="md">Card readers</Label>
                    <Paragraph size="sm" className="mt-[2px] text-gray-600">
                        Select
                    </Paragraph>
                </div>

                {readers.map((reader, idx) => (
                    <div
                        key={reader.id}
                        className={clsx(
                            'flex cursor-pointer items-center justify-between rounded-lg p-4 outline outline-gray-300 hover:bg-black/4 active:bg-black/8',
                            {
                                'outline-secondary-400 outline-2': selectedReaderID === reader.id,
                            }
                        )}
                        onClick={() => onSelectReader(reader)}
                    >
                        <div>
                            <Label size="md">{reader.label || `Card reader ${idx + 1}`}</Label>
                            <Paragraph size="sm" className="text-gray-600">
                                {reader.serialNumber}
                            </Paragraph>
                            {reader.isUpdated ? (
                                <Paragraph size="sm" className="text-gray-600">
                                    {'Up to date'}
                                </Paragraph>
                            ) : (
                                <Paragraph size="sm" className="text-danger-400">
                                    Update required
                                </Paragraph>
                            )}
                        </div>
                        <Checkbox
                            style="radio"
                            isChecked={selectedReaderID === reader.id}
                            className="shrink-0"
                        />
                    </div>
                ))}
            </div>
        </div>
    )
}

function PayByOther({
    payment,
    onUpdatePayment,
}: {
    payment: Payment
    onUpdatePayment: (p: Payment) => void
}) {
    payment.markPaidDate = payment.markPaidDate ?? moment()

    return (
        <div className="flex flex-col gap-8">
            <div className="flex gap-4">
                <Icon icon="cards.large" background="round" className="shrink-0" />
                <div>
                    <Label size="md">Payment method</Label>
                    <Paragraph size="sm" className="mt-[2px] text-gray-600">
                        Specify how this payment was made and optionally include a check or payment
                        number.
                    </Paragraph>

                    <div className="mt-3 flex flex-wrap gap-2">
                        {markPaidPaymentOptions.map((o) => (
                            <Chip
                                key={o.value}
                                title={o.label}
                                size="small"
                                onClick={() =>
                                    onUpdatePayment(
                                        payment.clone({
                                            paymeth: o.value,
                                            source: undefined,
                                        })
                                    )
                                }
                                isSelected={payment.paymeth === o.value}
                            />
                        ))}
                    </div>
                </div>
            </div>

            <WorkflowPage.IconSection icon="wallet.large" title="Payment number">
                <TextField
                    isFullWidth
                    size="medium"
                    placeholder="Enter payment number"
                    value={payment.refnum ?? ''}
                    onClear={() =>
                        onUpdatePayment(
                            payment.clone({
                                refnum: undefined,
                                source: undefined,
                            })
                        )
                    }
                    onChange={(e) =>
                        onUpdatePayment(
                            payment.clone({
                                refnum: e.target.value,
                                source: undefined,
                            })
                        )
                    }
                />
            </WorkflowPage.IconSection>

            <WorkflowPage.IconSection icon="calendar.large" title="Transaction date">
                <DatePicker
                    size="medium"
                    selectedDateString={
                        payment.markPaidDate
                            ? moment(payment.markPaidDate).format('MM/DD/YYYY')
                            : undefined
                    }
                    onSelectDate={(date) => {
                        if (!isSameDMY(date, payment.markPaidDate)) {
                            const clone = payment.clone()
                            clone.markPaidDate = moment(date)
                            clone.source = undefined
                            onUpdatePayment(clone)
                        }
                    }}
                />
            </WorkflowPage.IconSection>

            <WorkflowPage.IconSection
                icon="receipt.large"
                title="Send receipt"
                description="Send the client a receipt"
                hasHoverEffect
                secondaryView={
                    <Checkbox
                        isChecked={payment.sendReceipt}
                        onChange={(checked) => {
                            const clone = payment.clone()
                            clone.sendReceipt = checked
                            clone.source = undefined
                            onUpdatePayment(clone)
                        }}
                    />
                }
            />
        </div>
    )
}

const getAvailablePaymentMethods = (
    user: User,
    readers: Reader[],
    hasClient: boolean,
    canPayViaOther: boolean,
    canPayViaReader: boolean
) => {
    const paymentMethods: ComponentProps<typeof SegmentedControl>['segments'] = []

    if (hasClient) {
        paymentMethods.push({
            label: 'Card',
            value: 'card',
            icon: <Icon icon="bankCard.small" shouldInheritColor />,
        })
    }

    if (user.owner.allowReader && readers.length && canPayViaReader) {
        paymentMethods.push({
            label: 'Card reader',
            value: 'reader',
            icon: <Icon icon="cardReader.small" shouldInheritColor />,
        })
    }

    if (user.owner.allowMark && canPayViaOther) {
        paymentMethods.push({
            label: 'Other',
            value: 'other',
            icon: <Icon icon="cards.small" shouldInheritColor />,
        })
    }

    return paymentMethods
}

export function NewChargePaymentPage({
    payment,
    readers,
    onUpdatePayment,
    onSelectReader,
    selectedReader,
    canPayViaOther,
}: Props) {
    const me = PSApplication.me()
    const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState<
        'card' | 'reader' | 'other'
    >(selectedReader !== undefined ? 'reader' : payment.source !== undefined ? 'card' : 'other')

    const { data: sources, isLoading: isLoadingSources } = useQuery({
        queryKey: ['sources', payment.client?.id],
        queryFn: () => Source.validClientSources(payment.client?.id ?? ''),
    })

    const paymentMethods = useMemo(() => {
        const pm = getAvailablePaymentMethods(
            me,
            readers,
            payment.client !== undefined,
            canPayViaOther,
            payment.type !== 'subscription'
        )
        if (pm.length) {
            setSelectedPaymentMethodType(pm[0].value as 'card' | 'reader' | 'other')
        }
        return pm
    }, [me, readers, payment.client])

    if (isLoadingSources) {
        return (
            <div className="flex h-full items-center justify-center">
                <Spinner />
            </div>
        )
    }

    return (
        <div className="flex items-start justify-center gap-6">
            <Card size="lg" className="w-[744px] max-w-[744px] rounded-lg bg-white p-8">
                {paymentMethods.length > 1 && (
                    <SegmentedControl
                        className="mb-8 w-full"
                        size="medium"
                        segments={paymentMethods}
                        value={selectedPaymentMethodType}
                        onChange={(value) => {
                            if (value !== 'other') {
                                onUpdatePayment(payment.clone({ mark_paid_date: undefined }))
                            }
                            setSelectedPaymentMethodType(value as 'card' | 'reader' | 'other')
                        }}
                    />
                )}

                {selectedPaymentMethodType === 'card' && (
                    <PayByCard
                        sources={sources ?? []}
                        payment={payment}
                        onSelectSource={(s) => {
                            onUpdatePayment(
                                payment.clone({
                                    source: s,
                                    refnum: undefined,
                                    mark_paid_date: undefined,
                                    paymeth: undefined,
                                    send_receipt: undefined,
                                })
                            )
                            onSelectReader(undefined)
                        }}
                        selectedSourceID={payment.source?.id}
                    />
                )}
                {selectedPaymentMethodType === 'reader' && (
                    <PayByCardReader
                        readers={readers}
                        onSelectReader={(r) => {
                            onUpdatePayment(
                                payment.clone({
                                    source: undefined,
                                    refnum: undefined,
                                    mark_paid_date: undefined,
                                    paymeth: undefined,
                                    send_receipt: undefined,
                                })
                            )
                            onSelectReader(r)
                        }}
                        selectedReaderID={selectedReader?.id}
                    />
                )}
                {selectedPaymentMethodType === 'other' && (
                    <PayByOther
                        payment={payment}
                        onUpdatePayment={(p) => {
                            onUpdatePayment(p)
                            onSelectReader(undefined)
                        }}
                    />
                )}
            </Card>
            <div className="flex w-[360px] flex-col gap-6">
                {payment.client !== undefined && (
                    <Card size="sm">
                        <Heading size="xs" className="mb-6">
                            Client
                        </Heading>
                        <ContactAvatar
                            user={payment.client ?? new User({ name: '' })}
                            subtitle={payment.subaccount?.name}
                            showName={true}
                            truncateName={true}
                        />
                    </Card>
                )}

                <NewChargeReceiptView payment={payment} />
            </div>
        </div>
    )
}
